import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronLeft, Send, AlertTriangle } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { fetchUserWarnings, sendUserWarning, type UserWarning } from './userWarningsApi'

/**
 * A3.26 — Ogohlantirish yuborish. Foydalanuvchiga rasmiy ogohlantirish: durable
 * saqlanadi (tarix + audit) va PUSH (FCM/WebPush) orqali qurilmasiga yetkaziladi.
 * Yadro-chat (messages/dialogs) TEGILMAYDI — bu xavfsiz push yo'li. Matnlar lokal.
 */

const S = {
  title: 'Ogohlantirishlar',
  back: 'Karta',
  placeholder: "Foydalanuvchiga ogohlantirish matni (push orqali yetkaziladi)…",
  send: 'Ogohlantirish yuborish',
  sent: 'Ogohlantirish yuborildi',
  failed: 'Yuborilmadi',
  empty: 'Hozircha ogohlantirish yo‘q',
  by: 'Yuborgan',
  deliverNote: 'Push orqali yetkaziladi + tarixда saqlanadi (o‘chirilmaydi).',
}

function fmtTime(ts: number): string {
  if (!Number.isFinite(ts)) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return '—'
  }
}

export function UserWarnings() {
  const { id = '' } = useParams()
  const userId = Number(id)
  const { toast } = useToast()
  const [rows, setRows] = useState<UserWarning[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    fetchUserWarnings(userId, 100)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [userId])

  useEffect(() => {
    load()
  }, [load])

  const send = useCallback(async () => {
    const text = draft.trim()
    if (!text) return
    setSending(true)
    try {
      await sendUserWarning(userId, text)
      toast(S.sent, { tone: 'success' })
      setDraft('')
      load()
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    } finally {
      setSending(false)
    }
  }, [draft, userId, toast, load])

  return (
    <>
      <PageHeader
        title={S.title}
        screenId="A3.26"
        actions={
          <Link
            to={`/users/${id}`}
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.back}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto max-w-2xl space-y-3">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-amber-400">
              <AlertTriangle size={14} />
              {S.send}
            </div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={S.placeholder}
              rows={3}
              maxLength={2000}
              className="w-full resize-y rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-border-strong"
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-tertiary">{S.deliverNote}</span>
              <button
                onClick={() => void send()}
                disabled={sending || !draft.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-amber-500 disabled:opacity-50"
              >
                <Send size={14} />
                {S.send}
              </button>
            </div>
          </div>

          {rows === null || loading ? (
            <div className="p-6 text-center text-sm text-tertiary">…</div>
          ) : rows.length === 0 ? (
            <div className="px-3 py-10 text-center text-sm text-tertiary">
              <AlertTriangle size={24} className="mx-auto mb-2 opacity-60" />
              {S.empty}
            </div>
          ) : (
            <ul className="space-y-2">
              {rows.map((w) => (
                <li key={w.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="mb-1 flex items-center justify-between text-xs text-tertiary">
                    <span>{S.by}: {w.sender ? `@${w.sender}` : '—'}</span>
                    <span>{fmtTime(w.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm text-primary">{w.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  )
}
